import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';
import { Crown, Users, Camera, Building2 } from 'lucide-react';
import './Login.css';

const Login: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { login, user, loading: authLoading } = useAuth();
    const history = useHistory();
    const location = useLocation();

    // Handle initial role from query params
    const queryParams = new URLSearchParams(location.search);
    const initialRole = queryParams.get('role') as 'admin' | 'guest' | 'photographer' | 'agency';
    
    const [role, setRole] = useState<'admin' | 'guest' | 'photographer' | 'agency'>(
        initialRole && ['admin', 'guest', 'photographer', 'agency'].includes(initialRole) ? initialRole : 'admin'
    );
    
    // Redirect if already logged in
    React.useEffect(() => {
        if (!authLoading && user) {
            if (initialRole && user.role !== initialRole) {
                return;
            }
            if (user.role === 'agency') {
                window.location.href = '/agency/';
            } else {
                history.push('/welcome');
            }
        }
    }, [user, authLoading, history, initialRole]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Trim to handle any whitespace from mobile keyboards
            const cleanUsername = username.trim();
            const cleanPassword = password.trim();

            console.log('[Login] Attempting login for role:', role, 'username:', cleanUsername);

            await login(cleanUsername, cleanPassword, role);
            
            // If the user is an agency, redirect them directly to the agency portal
            if (role === 'agency') {
                window.location.href = '/agency/';
            } else {
                history.push('/welcome');
            }
        } catch (err: any) {
            console.error('[Login] Error:', err.message);
            setError(err.message || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h1 className="login-title">Welcome Back</h1>
                    <p className="login-subtitle">Sign in to manage your wedding</p>
                </div>

                <form className="login-form" onSubmit={handleSubmit}>
                    <div className="role-selection-wrapper">
                        <label className="role-label">I am a...</label>
                        <div className="role-cards-grid">
                            <div 
                                className={`role-card ${role === 'admin' ? 'active' : ''}`}
                                onClick={() => setRole('admin')}
                            >
                                <Crown size={24} />
                                <span>Wedding Admin</span>
                            </div>
                            <div 
                                className={`role-card ${role === 'guest' ? 'active' : ''}`}
                                onClick={() => setRole('guest')}
                            >
                                <Users size={24} />
                                <span>Guest</span>
                            </div>
                            <div 
                                className={`role-card ${role === 'photographer' ? 'active' : ''}`}
                                onClick={() => setRole('photographer')}
                            >
                                <Camera size={24} />
                                <span>Photographer</span>
                            </div>
                            <div 
                                className={`role-card ${role === 'agency' ? 'active' : ''}`}
                                onClick={() => setRole('agency')}
                            >
                                <Building2 size={24} />
                                <span>Agency</span>
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="username">Email or Mobile Number</label>
                        <input
                            type="text"
                            id="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Enter your email or mobile"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter your password"
                            required
                        />
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    <Button
                        label={loading ? 'Signing in...' : 'Sign In'}
                        onClick={() => { }}
                        disabled={loading}
                        className="login-button"
                    />
                </form>

                <div className="login-footer">
                    <div className="registration-options">
                        <p>Don't have an account?</p>
                        <div className="reg-buttons">
                            <button 
                                className="reg-btn-primary"
                                onClick={() => history.push('/register')}
                            >
                                Start Planning Your Wedding
                            </button>
                        </div>
                    </div>

                    <div className="agency-login-link">
                        <a 
                            href="/agency/register" 
                            className="pro-link"
                            onClick={(e) => { 
                                e.preventDefault(); 
                                history.push('/agency/register'); 
                            }}
                        >
                            Professional Vendor/Agency? Register here
                        </a>
                    </div>
                    
                    <div className="help-text">
                        Need help? Contact your wedding coordinator
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
